import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';

import * as Styled from './Pill.styled';

const TRANSITION_DURATION_ENTER = 200;
const TRANSITION_DURATION_EXIT = 100;

/**
 * `Pill` is a compact element that represent an input, attribute, or action.
 * Use `AnimatedPill` if you would like Pill to fade in/out on mount/unmount.
 */
export const Pill = ({ onDelete, variant, label }) => (
  <Styled.Pill as={onDelete && 'button'} variant={variant} onClick={onDelete}>
    <span>{label}</span>
    {onDelete && <Styled.Icon name="close" />}
  </Styled.Pill>
);

/**
 * `AnimatedPill` is a compact elements that represent an input, attribute, or action.
 * Use `Pill` if you would not like Pill to fade in/out on mount/unmount.
 */
export const AnimatedPill = ({ onDelete, ...props }) => {
  const [isAdded, setIsAdded] = useState(false);

  useEffect(() => {
    // Toggle isAdded to true to trigger the 'enter' animation
    setIsAdded(true);
  }, []);

  return (
    <CSSTransition
      in={isAdded}
      timeout={{
        enter: TRANSITION_DURATION_ENTER,
        exit: TRANSITION_DURATION_EXIT
      }}
      classNames="fade"
      onExited={onDelete}
    >
      <Styled.Fade
        transitionDurationEnter={TRANSITION_DURATION_ENTER}
        transitionDurationExit={TRANSITION_DURATION_EXIT}
      >
        <Pill onDelete={() => setIsAdded(false)} {...props} />
      </Styled.Fade>
    </CSSTransition>
  );
};

const propTypes = {
  /**
   * A function that is called when Pill is clicked.
   * If set, a delete icon will be shown.
   */
  onDelete: PropTypes.func,
  /**
   * Text to display inside Pill
   */
  label: PropTypes.string.isRequired,
  /**
   * Type of Pill to display
   */
  variant: PropTypes.oneOf(['default', 'dark', 'light', 'error'])
};

Pill.propTypes = propTypes;
AnimatedPill.propTypes = propTypes;

Pill.defaultProps = {
  onDelete: null,
  variant: 'default'
};
AnimatedPill.defaultProps = {
  onDelete: null,
  variant: 'default'
};

export default Pill;
